import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { getStates, getPlaceToday } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/home/ExploreByState";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const slugify = (n) => n.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

// 10 historical regions → distinct palette (ordered as on the legacy portal).
const REGION_COLORS = [
  { key: "new-england", label: "New England", color: "#0a2a5e" },
  { key: "mid-atlantic", label: "Mid-Atlantic", color: "#1d4e89" },
  { key: "appalachian-highlands", label: "Appalachian Highlands", color: "#2e7d32" },
  { key: "the-southeast", label: "The Southeast", color: "#c8102e" },
  { key: "great-lakes", label: "Great Lakes", color: "#0288a7" },
  { key: "the-heartland", label: "The Heartland", color: "#e0a100" },
  { key: "the-southwest", label: "The Southwest", color: "#d2691e" },
  { key: "mountain-states", label: "Mountain States", color: "#6a4c93" },
  { key: "pacific-coast", label: "Pacific Coast", color: "#00897b" },
  { key: "distant-shores", label: "Distant Shores", color: "#7a5c3e" },
];
const COLOR_BY_KEY = Object.fromEntries(REGION_COLORS.map((r) => [r.key, r.color]));
const LABEL_BY_KEY = Object.fromEntries(REGION_COLORS.map((r) => [r.key, r.label]));

export function RegionMap() {
  const [byState, setByState] = useState({});
  const [hover, setHover] = useState(null);
  const [tip, setTip] = useState({ x: 0, y: 0 });
  const [temps, setTemps] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getStates().then((d) => {
      const m = {};
      d.states.forEach((s) => { m[s.geo_id] = s; });
      setByState(m);
    }).catch(() => {});
  }, []);

  const onEnter = (slug) => {
    setHover(slug);
    if (temps[slug] === undefined) {
      setTemps((t) => ({ ...t, [slug]: null })); // mark in-flight
      getPlaceToday(slug).then((w) => {
        if (w?.available && w.temp_f !== undefined) setTemps((t) => ({ ...t, [slug]: w.temp_f }));
      }).catch(() => {});
    }
  };

  const hoverInfo = useMemo(() => (hover ? byState[hover] : null), [hover, byState]);

  return (
    <section className="mx-auto max-w-[1500px] px-4 py-16" data-testid="region-map-home">
      <SectionHeading
        kicker="America, by Historical Region"
        title="Explore the map, region by region"
        action={
          <Link to="/explore/states" className="hidden items-center gap-1.5 rounded-md border border-navy/20 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-navy hover:bg-navy hover:text-white sm:inline-flex" data-testid="view-all-states">
            <T>View All States</T>
          </Link>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-center">
        <div className="relative overflow-hidden rounded-2xl border border-navy/15 bg-white shadow-xl">
          <ComposableMap projection="geoAlbersUsa" width={980} height={560} style={{ width: "100%", height: "auto" }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const slug = slugify(geo.properties.name);
                  const st = byState[slug];
                  const regionKey = st?.historical_region;
                  const base = regionKey ? COLOR_BY_KEY[regionKey] : "#e8dcc4";
                  const isHover = hover === slug;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => st && onEnter(slug)}
                      onMouseLeave={() => setHover(null)}
                      onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY })}
                      onClick={() => regionKey && navigate(`/region/${regionKey}`)}
                      data-testid={`region-map-state-${slug}`}
                      style={{
                        default: { fill: base, stroke: "#fff", strokeWidth: 0.6, outline: "none", opacity: isHover ? 1 : 0.9, transition: "opacity 0.2s" },
                        hover: { fill: base, stroke: "#fff", strokeWidth: 1, outline: "none", opacity: 1, cursor: regionKey ? "pointer" : "default" },
                        pressed: { fill: base, outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          {hoverInfo && (
            <div className="pointer-events-none fixed z-50 rounded-md bg-navy px-3 py-1.5 text-sm font-semibold text-white shadow-lg" style={{ left: tip.x + 14, top: tip.y + 14 }}>
              {hoverInfo.official_name}
              {typeof temps[hover] === "number" && <span className="ml-2 font-black text-amber-300">{temps[hover]}°F</span>}
              {hoverInfo.historical_region && <span className="ml-2 text-xs font-normal text-white/70">{LABEL_BY_KEY[hoverInfo.historical_region]}</span>}
            </div>
          )}
        </div>

        <div>
          <p className="mb-4 max-w-md text-sm text-muted-foreground">
            <T>The ten historical regions recovered from a classic 2012 state portal. Click a region to open its hub, or a state to dive in.</T>
          </p>
          <div className="grid grid-cols-2 gap-2" data-testid="region-legend">
            {REGION_COLORS.map((r) => (
              <Link key={r.key} to={`/region/${r.key}`} className="group flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-navy transition-colors hover:bg-parchment" data-testid={`region-legend-${r.key}`}>
                <span className="h-3.5 w-3.5 shrink-0 rounded-sm ring-1 ring-black/10" style={{ backgroundColor: r.color }} />
                <span className="truncate group-hover:text-oldglory-red"><T>{r.label}</T></span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default RegionMap;
